"use client";

import { type ReactNode, useState } from "react";
import { LuLoader2 } from "react-icons/lu";
import { BackdropSwatches } from "@/components/photo-studio/backdrop-picker";
import { Badge } from "@/components/ui";
import { useUser } from "@/contexts/user-provider";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { ensureStudioMask } from "@/lib/photo-studio/studio-photo";
import { toast } from "@/lib/use-toast";
import { STUDIO_BACKDROPS, type StudioBackdrop } from "@/types/photo-studio";
import { useAttachmentStudio, usePhotoStudioSettings, useSetAttachmentBackdrop, useStudioRender } from "@/api/provider/photo-studio/studio-query";
const REFINE_LABELS: Partial<Record<string, string>> = {
  PENDING: "Sharpening edges…",
  RUNNING: "Sharpening edges…",
  FAILED: "Edge sharpening failed"
};

/**
 * Studio Mode controls for a single-photo viewer: one row of backdrop swatches
 * (Original first) and the edge-sharpening status. Staff who can edit notes
 * save the backdrop they pick; everyone else can only switch between the
 * photo's backdrop and Original. Original never saves: it shows the photo as
 * taken, for this viewer only. `showAsTaken` is the action that saves it. `imageUrl` is the Studio
 * version to show instead of the original, or null to show the original.
 */
export function useStudioViewer(attachmentId: string | null | undefined, {
  open,
  isImage
}: {
  open: boolean;
  isImage: boolean;
}): {
  imageUrl: string | null;
  isTransparent: boolean;
  controls: ReactNode;
  // Saves the photo with no backdrop; null when the viewer can't or there's nothing to remove.
  showAsTaken: (() => void) | null;
} {
  const enabled = open && isImage && !!attachmentId;
  const {
    currentUser
  } = useUser();
  const canEdit = hasPermission(currentUser, PERMISSIONS.PATIENTS_NOTES_WRITE);
  // Which photo is being peeked at as taken, so the peek ends on the next photo.
  const [originalFor, setOriginalFor] = useState<string | null>(null);
  const showOriginal = !!attachmentId && originalFor === attachmentId;
  const setShowOriginal = (on: boolean) => setOriginalFor(on ? attachmentId ?? null : null);
  const [isPreparing, setIsPreparing] = useState(false);
  const {
    data: settings
  } = usePhotoStudioSettings(enabled);
  const {
    data: state
  } = useAttachmentStudio(attachmentId, enabled);
  const setBackdrop = useSetAttachmentBackdrop();
  const hasStudioVersion = !!state?.isImage && state.hasMask && state.effectiveBackdrop !== "NONE";
  const {
    data: render,
    isFetching: isRendering
  } = useStudioRender(attachmentId, {
    size: "FULL"
  }, enabled && !showOriginal && hasStudioVersion);
  if (!enabled || !state?.isImage) return {
    imageUrl: null,
    isTransparent: false,
    controls: null,
    showAsTaken: null
  };
  const handleBackdropChange = async (backdrop: StudioBackdrop) => {
    try {
      if (backdrop !== "NONE" && !state.hasMask) {
        setIsPreparing(true);
        const found = await ensureStudioMask(state);
        if (!found) {
          toast({
            title: "No one found in this photo",
            description: "Studio needs a person in the photo to replace the background.",
            variant: "warning"
          });
          return;
        }
      }
      await setBackdrop.mutateAsync({
        attachmentId: state.attachmentId,
        backdrop
      });
    } catch (error) {
      console.error("Failed to change the Studio backdrop:", error);
      toast({
        title: "Backdrop not changed",
        description: "Something went wrong. Please try again.",
        variant: "error"
      });
    } finally {
      setIsPreparing(false);
    }
  };
  const refineLabel = state.refineStatus ? REFINE_LABELS[state.refineStatus] : undefined;
  const busy = isPreparing || setBackdrop.isPending;
  const shown: StudioBackdrop = showOriginal || !hasStudioVersion ? "NONE" : state.effectiveBackdrop;
  const readOnlyExclude = STUDIO_BACKDROPS.filter(b => b !== "NONE" && b !== state.effectiveBackdrop);
  const controls = <div className="flex flex-wrap items-center gap-3">
      {canEdit ? <BackdropSwatches value={shown} onChange={backdrop_0 => {
      // Original is a peek at the photo as taken; it saves nothing.
      if (backdrop_0 === "NONE") {
        setShowOriginal(true);
        return;
      }
      setShowOriginal(false);
      void handleBackdropChange(backdrop_0);
    }} clinicDefault={settings?.defaultBackdrop} brandColor={settings?.effectiveBrandColor} disabled={busy} aria-label="Show photo on" /> : hasStudioVersion && <BackdropSwatches value={shown} onChange={backdrop_1 => setShowOriginal(backdrop_1 === "NONE")} exclude={readOnlyExclude} brandColor={settings?.effectiveBrandColor} aria-label="Show photo on" />}
      {(busy || !showOriginal && hasStudioVersion && isRendering) && <LuLoader2 className="size-4 animate-spin text-foreground-secondary" aria-label="Loading Studio photo" />}
      {refineLabel && <Badge variant={state.refineStatus === "FAILED" ? "warning" : "default"}>
          {refineLabel}
        </Badge>}
    </div>;
  const imageUrl = !showOriginal && hasStudioVersion ? render?.signedUrl ?? null : null;
  const isTransparent = !!imageUrl && state.effectiveBackdrop === "TRANSPARENT";
  const showAsTaken = canEdit && hasStudioVersion && !busy ? () => {
    setShowOriginal(false);
    void handleBackdropChange("NONE");
  } : null;
  return {
    imageUrl,
    isTransparent,
    controls,
    showAsTaken
  };
}